import type { ArchView } from './nav'

/** Public URL vocabulary, independent of the internal view/tab codes. */
export const ARCH_PATHS: Record<ArchView, { path: string; title: string; tabs: Record<string, string> }> = {
  overview: { path: 'overview', title: '架构总览', tabs: { stats: '', blueprint: 'blueprint', relations: 'relations', types: 'metadata', bind: 'navigation-bindings' } },
  BA: { path: 'ba', title: '业务架构', tabs: { vs: 'value-streams', cap: 'capabilities', rp: 'processes', ep: 'scenarios', rule: 'rules', val: 'validations', goal: 'goals', role: 'roles', sm: 'state-machines', evt: 'events' } },
  AA: { path: 'aa', title: '应用架构', tabs: { tree: 'apps', svc: 'services' } },
  DA: { path: 'da', title: '数据架构', tabs: { concept: 'concepts', obj: 'business-objects', ref: 'reference-codes', ent: 'entities', flow: 'data-flows' } },
  TA: { path: 'ta', title: '技术架构', tabs: { net: 'network', dep: 'deployments', sw: 'software', impl: 'implementation-mappings' } },
  product: { path: 'product', title: '产品设计', tabs: { comp: 'components', tpl: 'templates', pattern: 'patterns', menu: 'menus', func: 'functions', cfg: 'configuration' } },
  dev: { path: 'development', title: '开发测试', tabs: { api: 'apis', fn: 'functions', phy: 'tables', evt: 'events', op: 'operations', test: 'test-cases' } },
  ops: { path: 'operations', title: '运营维护', tabs: { issue: 'issues', em: 'emergencies', backup: 'backups' } },
  spec: { path: 'standards', title: '原则规范', tabs: { fe: 'frontend', be: 'backend', da: 'data', wf: 'workflow' } },
  govern: { path: 'governance', title: '架构治理', tabs: { rules: '', violations: 'violations', adj: 'adjustments', elements: 'elements', changes: 'changes', impact: 'impact', trace: 'traces', integrity: 'integrity', matrix: 'matrix' } }
}

type Query = Record<string, any>
function objectKey(view: ArchView, tab: string) {
  if (view === 'AA' && tab === 'tree') return 'app'
  if (view === 'BA') return ['vs', 'cap', 'rp', 'ep', 'rule', 'val'].includes(tab) ? 'sel' : 'pick'
  if (view === 'DA') return ['concept', 'obj', 'ref'].includes(tab) ? 'obj' : 'pick'
  return 'pick'
}

export function architectureLocation(input: Query) {
  const query = { ...input }
  const view = (query.view || 'overview') as ArchView
  const config = ARCH_PATHS[view]
  if (!config) return null
  const tab = String((view === 'AA' ? query.section : query.tab) || Object.keys(config.tabs)[0])
  if (!(tab in config.tabs)) return null
  let path = `/arch/${config.path}`
  const overview = ['AA', 'BA', 'DA'].includes(view) && query.pane !== 'detail' &&
    !['app', 'sel', 'nd', 'obj', 'dom', 'grp', 'sub', 'l1', 'pick'].some(k => query[k])
  const defaultProductPage = view === 'product' && tab === 'comp' && !query.pick
  const sectionPath = !defaultProductPage && (!overview || tab !== Object.keys(config.tabs)[0])
  if (sectionPath && config.tabs[tab]) path += `/${config.tabs[tab]}`
  if (overview && sectionPath) query.display = 'overview'
  else delete query.display
  const key = objectKey(view, tab)
  if (!overview && query[key] && view !== 'overview') {
    path += `/${encodeURIComponent(query[key])}`
    delete query[key]
  }
  delete query.view; delete query.tab; delete query.section; delete query.pane
  return { path, query }
}

/** Path determines the view/tab. Unknown paths are rejected, never silently defaulted. */
export function architectureRouteQuery(path: string, input: Query): Query | null {
  const parts = path.split('/').filter(Boolean)
  const entry = Object.entries(ARCH_PATHS).find(([, c]) => c.path === parts[1])
  if (parts[0] !== 'arch' || !entry || parts.length > 4) return null
  const [view, config] = entry as [ArchView, typeof ARCH_PATHS[ArchView]]
  const selected = parts[2] ? Object.entries(config.tabs).find(([, p]) => p === parts[2]) : Object.entries(config.tabs)[0]
  if (!selected || (view === 'overview' && parts[3])) return null
  const query: Query = { ...input, view, pane: !parts[2] || input.display === 'overview' ? 'overview' : 'detail', tab: selected[0], section: selected[0] }
  if (parts[3]) {
    try { query[objectKey(view, selected[0])] = decodeURIComponent(parts[3]) }
    catch { return null }
  }
  return query
}
